import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  DataGrid,
  ExportCsv,
  Toolbar,
} from "@mui/x-data-grid";

import { useQuery, useQueryClient } from "react-query";

import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

import Constants from "../services/Constants";
import ExportOption from "./common/ExportOption";
import { GoToPageControls } from "./common/GoToPageToolbar";

const EMPTY_ARRAY = [];

/**
 * Returns true when two arrays of filter items are functionally equal.
 */
function areFilterItemsEqual(itemsA = [], itemsB = []) {
  const listA = Array.isArray(itemsA) ? itemsA : [];
  const listB = Array.isArray(itemsB) ? itemsB : [];

  if (listA.length !== listB.length) {
    return false;
  }

  return listA.every((itemA, index) => {
    const itemB = listB[index];
    if (!itemB) return false;

    const fieldA = itemA?.field || itemA?.columnField;
    const fieldB = itemB?.field || itemB?.columnField;
    const opA = itemA?.operator || itemA?.operatorValue;
    const opB = itemB?.operator || itemB?.operatorValue;
    const valA = itemA?.value;
    const valB = itemB?.value;

    return fieldA === fieldB && opA === opB && valA === valB;
  });
}

/**
 * Returns true when a filter value contains at least one
 * filter item with a meaningful value.
 */
function containsActiveFilter(filterValue) {
  return (
    filterValue?.items?.some((item) => {
      const value = item?.value;

      return (
        value !== undefined &&
        value !== null &&
        value !== ""
      );
    }) ?? false
  );
}

function CustomToolbar({
  onRefresh,
  exportCSV,
  exportProps,
  endpointFn,
  queryName,
  tableTitle,
  defaultExport,
  defaultCsvOptions,
  onClearFilters,
  hasFilter,
  totalPages,
  onPageChange,
  customActions,
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleToolbarRefresh = useCallback(async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);

    try {
      await onRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, onRefresh]);

  return (
    <Toolbar
      aria-label={`${tableTitle || "Data grid"} toolbar`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,

        width: "100%",
        minHeight: 58,
        px: 2.5,
        py: 1,

        borderBottom: "1px solid",
        borderColor: "divider",

        overflow: "visible",

        flexWrap: {
          xs: "wrap",
          md: "nowrap",
        },
      }}
    >
      <Typography
        variant="h6"
        component="div"
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          lineHeight: 1.5,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {tableTitle}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,

          ml: "auto",
          minWidth: 0,
          flexShrink: 0,

          flexWrap: {
            xs: "wrap",
            md: "nowrap",
          },

          "& .MuiButton-root": {
            whiteSpace: "nowrap",
          },
        }}
      >
        <GoToPageControls
          totalPages={totalPages}
          onPageChange={onPageChange}
        />

        <Tooltip
          title={
            hasFilter
              ? "Clear filters"
              : "No filters applied"
          }
          placement="top"
        >
          <span>
            <IconButton
              size="small"
              onClick={onClearFilters}
              disabled={!hasFilter}
              aria-label="Clear table filters"
              sx={{
                width: 34,
                height: 34,
              }}
            >
              <FilterAltOffIcon
                fontSize="small"
                color={
                  hasFilter
                    ? "primary"
                    : "disabled"
                }
              />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={
            isRefreshing
              ? "Refreshing..."
              : "Refresh"
          }
          placement="top"
        >
          <span>
            <IconButton
              size="small"
              onClick={handleToolbarRefresh}
              disabled={isRefreshing}
              aria-label="Refresh table data"
              sx={{
                width: 34,
                height: 34,
              }}
            >
              <RefreshIcon
                fontSize="small"
                color={
                  isRefreshing
                    ? "disabled"
                    : "primary"
                }
                sx={{
                  animation: isRefreshing
                    ? "ota-refresh-spin 0.8s linear infinite"
                    : "none",

                  "@keyframes ota-refresh-spin": {
                    from: {
                      transform: "rotate(0deg)",
                    },
                    to: {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {exportCSV ? (
          <ExportOption
            data={exportProps}
            endpointFn={endpointFn}
            queryName={queryName}
          />
        ) : defaultExport ? (
          <ExportCsv
            options={defaultCsvOptions}
          />
        ) : null}

        {customActions}
      </Box>
    </Toolbar>
  );
}

const OTAPagination = (props) => {
  const {
    endpointFn,
    queryName,
    sortByDefault,
    columns,
    extraFilters = EMPTY_ARRAY,
    exportRowsData,

    allVehicles = false,
    fetchAll = false,
    exportCSV = false,

    tableTitle,
    defaultExport = false,
    defaultCsvOptions,

    onSuccess,
    filters,
    extraQueriesToInvalidate = EMPTY_ARRAY,
    resetFilters = 0,
    onFilterApplied,
    onFiltersCleared,

    refetchInterval = 50000,

    slots: callerSlots,
    slotProps: callerSlotProps,

    newSx: sxFromProps,

    ...restProps
  } = props;

  const queryClient = useQueryClient();
  const [data, setData] = useState({
    rowCount: 0,
    pageSize: Constants.PAGE_SIZE,
    page: 1,
    field: sortByDefault,
    sort: "desc",
    filterValue: {
      items: [],
    },
  });

  const {
    filterValue,
    page,
    pageSize,
    field,
    sort,
    rowCount,
  } = data;

  useEffect(() => {
    if (filters === undefined) {
      return;
    }

    setData((previousData) => {
      const currentItems = previousData.filterValue?.items || [];
      if (areFilterItemsEqual(currentItems, filters)) {
        return previousData;
      }

      return {
        ...previousData,
        page: 1,
        filterValue: {
          items: [...filters],
        },
      };
    });
  }, [filters]);

  /**
   * Clear filters when resetFilters changes.
   */
  useEffect(() => {
    if (!resetFilters) {
      return;
    }

    setData((previousData) => {
      if ((previousData.filterValue?.items?.length || 0) === 0 && previousData.page === 1) {
        return previousData;
      }

      return {
        ...previousData,
        page: 1,
        filterValue: {
          items: [],
        },
      };
    });
  }, [resetFilters]);

  const hasFilter = useMemo(
    () => containsActiveFilter(filterValue),
    [filterValue]
  );
  const queryKey = useMemo(
    () => [
      queryName,
      page,
      pageSize,
      field,
      sort,
      filterValue,
      columns,
      extraFilters,
      fetchAll,
      allVehicles,
    ],
    [
      queryName,
      page,
      pageSize,
      field,
      sort,
      filterValue,
      columns,
      extraFilters,
      fetchAll,
      allVehicles,
    ]
  );

  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = useQuery(queryKey, endpointFn, {
    refetchOnWindowFocus: false,
    refetchInterval,
    keepPreviousData: true,

    onSuccess: (response) => {
      const itemCount =
        response?.data?.meta?.itemCount ?? 0;

      const responseRows =
        response?.data?.res ?? [];

      /*
       * Functional update prevents an older request callback
       * from overwriting newer pagination or filter state.
       */
      setData((previousData) => {
        if (previousData.rowCount === itemCount) {
          return previousData;
        }
        return {
          ...previousData,
          rowCount: itemCount,
        };
      });

      exportRowsData?.(responseRows);
      onSuccess?.(response);
    },
  });

  const paginationData =
    queryResponse?.data?.res ?? EMPTY_ARRAY;

  /**
   * Handle server-side sorting.
   */
  const handleSortModelChange = useCallback(
    (newSortModel) => {
      const sortData = newSortModel[0] ?? {
        field: sortByDefault,
        sort: "desc",
      };

      setData((previousData) => {
        const nextSort = sortData.sort ?? "desc";

        if (
          previousData.field === sortData.field &&
          previousData.sort === nextSort &&
          previousData.page === 1
        ) {
          return previousData;
        }

        return {
          ...previousData,
          page: 1,
          field: sortData.field,
          sort: nextSort,
        };
      });
    },
    [sortByDefault]
  );

  /**
   * Handle server-side filtering.
   */
  const handleFilterModelChange = useCallback(
    (newFilterValue) => {
      setData((previousData) => {
        const prevItems = previousData.filterValue?.items || [];
        const newItems = newFilterValue?.items || [];

        if (areFilterItemsEqual(prevItems, newItems)) {
          return previousData;
        }

        return {
          ...previousData,
          page: 1,
          filterValue: newFilterValue,
        };
      });

      if (containsActiveFilter(newFilterValue)) {
        onFilterApplied?.();
      } else {
        onFiltersCleared?.();
      }
    },
    [onFilterApplied, onFiltersCleared]
  );

  /**
   * Clear the complete DataGrid filter value.
   */
  const handleClearFilters = useCallback(() => {
    setData((previousData) => {
      if (
        !containsActiveFilter(previousData.filterValue) &&
        previousData.page === 1
      ) {
        return previousData;
      }

      return {
        ...previousData,
        page: 1,
        filterValue: {
          items: [],
        },
      };
    });

    onFiltersCleared?.();
  }, [onFiltersCleared]);

  /**
   * Invalidate this table's query and any related queries.
   */
  const handleRefresh = useCallback(async () => {
    const queryNames = [
      queryName,
      ...extraQueriesToInvalidate,
    ].filter(Boolean);

    await Promise.all(
      queryNames.map((query) =>
        queryClient.invalidateQueries([query])
      )
    );
  }, [
    queryClient,
    queryName,
    extraQueriesToInvalidate,
  ]);

  /**
   * Always provide at least one page to GoToPageControls.
   */
  const totalPages = Math.max(
    1,
    Math.ceil(rowCount / pageSize)
  );

  /**
   * GoToPageControls returns a one-based page number.
   */
  const handleGoToPage = useCallback(
    (requestedPage) => {
      const parsedPage = Number(requestedPage);

      if (!Number.isInteger(parsedPage)) {
        return;
      }

      const boundedPage = Math.min(
        Math.max(parsedPage, 1),
        totalPages
      );

      setData((previousData) => {
        if (previousData.page === boundedPage) {
          return previousData;
        }
        return {
          ...previousData,
          page: boundedPage,
        };
      });
    },
    [totalPages]
  );

  /**
   * Parameters for the server-side ExportOption component.
   */
  const exportProps = useMemo(
    () => [
      `${queryName}Export`,
      page,
      pageSize,
      field,
      sort,
      filterValue,
      columns,
      extraFilters,
      true,
      allVehicles,
    ],
    [
      queryName,
      page,
      pageSize,
      field,
      sort,
      filterValue,
      columns,
      extraFilters,
      allVehicles,
    ]
  );

  const callerSx = Array.isArray(sxFromProps)
    ? sxFromProps.filter(Boolean)
    : sxFromProps
      ? [sxFromProps]
      : [];

  return (
    <DataGrid
      columns={columns}
      rows={paginationData}
      rowCount={rowCount}
      loading={isLoading || isFetching}

      pagination
      paginationMode="server"
      sortingMode="server"
      filterMode="server"

      initialState={{
        sorting: {
          sortModel: [
            {
              field: sortByDefault,
              sort: "desc",
            },
          ],
        },
      }}

      paginationModel={{
        page: page - 1,
        pageSize,
      }}

      pageSizeOptions={
        Constants.ROWS_PER_PAGE_OPT
      }

      onPaginationModelChange={(model) => {
        setData((previousData) => {
          const pageSizeChanged =
            model.pageSize !== previousData.pageSize;

          const nextPage = pageSizeChanged
            ? 1
            : model.page + 1;

          if (
            previousData.page === nextPage &&
            previousData.pageSize === model.pageSize
          ) {
            return previousData;
          }

          return {
            ...previousData,
            page: nextPage,
            pageSize: model.pageSize,
          };
        });
      }}

      onSortModelChange={
        handleSortModelChange
      }

      filterModel={filterValue}

      onFilterModelChange={
        handleFilterModelChange
      }

      showToolbar

      slots={{
        ...callerSlots,

        /*
         * Override the toolbar slot with this component's
         * custom toolbar.
         */
        toolbar: CustomToolbar,
      }}

      slotProps={{
        ...callerSlotProps,

        toolbar: {
          /*
           * Preserve additional toolbar props supplied
           * by the parent.
           */
          ...callerSlotProps?.toolbar,

          onRefresh: handleRefresh,

          exportCSV,
          exportProps,
          endpointFn,
          queryName,
          tableTitle,
          defaultExport,
          defaultCsvOptions,

          onClearFilters: handleClearFilters,
          hasFilter,

          totalPages,
          onPageChange: handleGoToPage,

          customActions: callerSlotProps?.toolbar?.customActions,
        },
      }}

      sx={[
        {
          flex: 1,
          minHeight: 700,
          border: "none",
          "& [role='toolbar']": {
            overflow: "visible",
          },
        },
        ...callerSx,
      ]}

      /*
       * Keep this last so parent components can continue
       * passing supported DataGrid properties such as:
       *
       * getRowId
       * checkboxSelection
       * onRowSelectionModelChange
       * disableRowSelectionOnClick
       */
      {...restProps}
    />
  );
};

OTAPagination.defaultProps = {
  sortByDefault: "createdAt",

  checkboxSelection: false,
  fetchAll: false,
  allVehicles: false,
  exportCSV: false,

  defaultExport: false,
  defaultCsvOptions: {
    allColumns: false,
    utf8WithBom: true,
  },

  endpointFn: () => {},
  queryName: "",

  extraQueriesToInvalidate: EMPTY_ARRAY,
  resetFilters: 0,
};

export default OTAPagination;